import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import ThemeSwitcher from './ThemeSwitcher';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import NavLinks from './NavLinks';
import UserMenu from './UserMenu';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileMenuToggle from './MobileMenuToggle';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationBell from './NotificationBell';
import { User, Settings, MessageSquare, LogOut } from 'lucide-react';
import { toast } from '@/utils/toast';
import { useChat } from '@/contexts/ChatContext';
import { useDeviceSafetyCheck } from '@/hooks/use-device-safety-check';

const Navbar: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isMobile = useIsMobile();
  const { toggleMicrophone, toggleVideo } = useChat();
  
  const { forceDisableDevices } = useDeviceSafetyCheck(false, true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    
    if (!location.pathname.includes('/chat') && !location.pathname.includes('/live-chat')) {
      console.log("Navbar: Navigated away from chat - disabling devices");
      toggleMicrophone(false);
      toggleVideo(false);
      forceDisableDevices();
    }
  }, [location.pathname, toggleMicrophone, toggleVideo, forceDisableDevices]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      console.log("Navbar: Logging out - disabling devices");
      toggleMicrophone(false);
      toggleVideo(false);
      forceDisableDevices();
      
      await logout();
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const navbarBgClass = isDark
    ? scrolled 
      ? "bg-[#0F1225]/90 backdrop-blur-xl shadow-md border-b border-[#6C41E0]/10" 
      : "bg-[#0F1225]/80 backdrop-blur-xl border-b border-[#6C41E0]/5"
    : scrolled 
      ? "bg-[#F5F5F7]/90 backdrop-blur-xl shadow-sm border-b border-[#D2D2D7]/30" 
      : "bg-[#F5F5F7]/80 backdrop-blur-xl border-b border-[#D2D2D7]/20";

  return (
    <header className={cn(
      "sticky top-0 left-0 right-0 z-50 transition-all duration-300 py-3",
      navbarBgClass
    )}>
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8">
        <div className="flex items-center gap-8 md:gap-14">
          <Logo />
          
          <div className="hidden md:block">
            <NavLinks />
          </div>
        </div>
        
        <div className="flex items-center space-x-3 md:space-x-4">
          {currentUser && <NotificationBell />}
          
          <ThemeSwitcher minimal={true} />
          
          <div className="hidden md:block">
            <UserMenu />
          </div>
          
          <div className="md:hidden">
            <MobileMenuToggle 
              isOpen={mobileMenuOpen}
              onClick={toggleMobileMenu}
              className={cn(
                isDark 
                  ? "text-gray-300 hover:bg-[#6C41E0]/10" 
                  : "text-[#1D1D1F] hover:bg-[#0066CC]/10"
              )}
            />
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={cn(
              "md:hidden fixed left-0 right-0 overflow-hidden z-50",
              isDark
                ? "bg-[#0F1225]/95 backdrop-blur-2xl border-b border-[#6C41E0]/10 shadow-md"
                : "bg-[#F5F5F7]/95 backdrop-blur-2xl border-b border-[#D2D2D7]/30 shadow-md"
            )}
            style={{ top: '60px' }}
          >
            <div className="flex flex-col py-4 px-6 overflow-y-auto max-h-[calc(100vh-150px)]">
              <NavLinks mobile={true} onLinkClick={() => setMobileMenuOpen(false)} />
              
              {currentUser ? (
                <div className={cn(
                  "mt-3 pt-4 flex flex-col gap-2", 
                  isDark ? "border-t border-[#6C41E0]/10" : "border-t border-[#D2D2D7]/30"
                )}>
                  <div className="flex items-center gap-3 px-2 py-2 mb-2">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center",
                      isDark ? "bg-[#292D67]" : "bg-blue-100"
                    )}>
                      <User size={18} className={isDark ? "text-white" : "text-blue-800"} />
                    </div>
                    <div className="flex flex-col">
                      <p className={cn(
                        "font-medium",
                        isDark ? "text-white" : "text-slate-800"
                      )}>
                        {currentUser.displayName || 'User'}
                      </p>
                      <p className={cn(
                        "text-xs",
                        isDark ? "text-slate-300" : "text-slate-500"
                      )}>
                        {currentUser.email}
                      </p>
                    </div>
                  </div>
                  
                  {[
                    { 
                      icon: <User size={18} />, 
                      text: 'Profile', 
                      onClick: () => navigate('/profile'),
                      textColor: isDark ? "text-white" : "text-slate-800"
                    },
                    { 
                      icon: <Settings size={18} />, 
                      text: 'Settings', 
                      onClick: () => navigate('/settings'),
                      textColor: isDark ? "text-white" : "text-slate-800"
                    },
                    { 
                      icon: <MessageSquare size={18} />, 
                      text: 'Chat', 
                      onClick: () => navigate('/chat'),
                      textColor: isDark ? "text-white" : "text-slate-800"
                    },
                    { 
                      icon: <LogOut size={18} />, 
                      text: 'Log out', 
                      onClick: handleLogout,
                      textColor: isDark ? "text-red-400" : "text-red-500"
                    }
                  ].map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.onClick();
                        setMobileMenuOpen(false);
                      }}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-md w-full text-left",
                        "transition-colors",
                        isDark 
                          ? "hover:bg-[#292D67]/60" 
                          : "hover:bg-slate-100",
                        item.textColor
                      )}
                    >
                      {item.icon}
                      <span>{item.text}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className={cn(
                  "pt-4 mt-4 flex flex-col gap-3", 
                  isDark ? "border-t border-[#6C41E0]/10" : "border-t border-[#D2D2D7]/30"
                )}>
                  <Link 
                    to="/login" 
                    className="block w-full"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button 
                      variant="outline" 
                      className={cn(
                        "w-full font-semibold", 
                        isDark 
                          ? "bg-transparent border-[#6C41E0]/20 text-white hover:bg-[#6C41E0]/10" 
                          : "bg-transparent border-[#0066CC]/20 text-[#0066CC] hover:bg-[#0066CC]/10"
                      )}
                    >
                      Sign In
                    </Button>
                  </Link>
                  <Link 
                    to="/signup" 
                    className="block w-full"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button 
                      variant="premium" 
                      className={cn(
                        "w-full font-semibold",
                        isDark 
                          ? "bg-[#6C41E0] hover:bg-[#5935C0] text-white" 
                          : "bg-[#0066CC] hover:bg-[#0055BB] text-white"
                      )}
                    >
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
